(() => {
  if (customElements.get('rdb-option-carousel')) return;
  class RdbOptionCarousel extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.viewport = this.querySelector('[data-option-viewport]');
      if (!this.viewport) return;
      this.previous = this.querySelector('[data-option-previous]');
      this.next = this.querySelector('[data-option-next]');
      this.previous.addEventListener('click', () => this.advance(-1), { signal });
      this.next.addEventListener('click', () => this.advance(1), { signal });
      this.viewport.addEventListener('scroll', () => this.update(), { signal, passive: true });
      this.viewport.addEventListener('keydown', (event) => {
        if (event.target !== this.viewport) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.advance((event.key === 'ArrowRight' ? 1 : -1) * (this.rtl ? -1 : 1));
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.viewport.scrollTo({ left: event.key === 'Home' ? 0 : (this.rtl ? -1 : 1) * this.viewport.scrollWidth, behavior: this.behavior });
        }
      }, { signal });
      this.viewport.addEventListener('focusin', (event) => {
        const option = event.target.closest('label');
        if (!option) return;
        const frame = this.viewport.getBoundingClientRect(), box = option.getBoundingClientRect();
        const delta = box.left < frame.left + 4 ? box.left - frame.left - 4 : box.right > frame.right - 4 ? box.right - frame.right + 4 : 0;
        if (delta) this.viewport.scrollBy({ left: delta, behavior: 'instant' });
      }, { signal });
      this.viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.suppressClick = false;
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.viewport.scrollLeft, active: false };
      }, { signal });
      this.viewport.addEventListener('pointermove', (event) => {
        const drag = this.drag;
        if (!drag || event.pointerId !== drag.id) return;
        const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
        if (!drag.active) {
          if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) { this.drag = null; return; }
          if (Math.abs(dx) < 8) return;
          drag.active = true;
          this.viewport.setPointerCapture(event.pointerId);
          this.viewport.classList.add('is-dragging');
        }
        event.preventDefault();
        this.viewport.scrollLeft = drag.left - dx;
      }, { signal });
      const release = () => {
        if (this.drag?.active) {
          this.suppressClick = true;
          if (this.viewport.hasPointerCapture(this.drag.id)) this.viewport.releasePointerCapture(this.drag.id);
        }
        this.drag = null;
        this.viewport.classList.remove('is-dragging');
      };
      this.viewport.addEventListener('pointerup', release, { signal });
      this.viewport.addEventListener('pointercancel', release, { signal });
      this.viewport.addEventListener('lostpointercapture', release, { signal });
      this.viewport.addEventListener('pointerleave', () => { if (!this.drag?.active) this.drag = null; }, { signal });
      this.viewport.addEventListener('click', (event) => {
        if (!this.suppressClick || event.detail === 0) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.suppressClick = false;
      }, { signal, capture: true });
      this.resize = new ResizeObserver(() => this.update());
      this.resize.observe(this.viewport);
      this.update();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resize?.disconnect();
    }
    get rtl() { return getComputedStyle(this.viewport).direction === 'rtl'; }
    get behavior() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'; }
    advance(direction) {
      this.viewport.scrollBy({ left: direction * (this.rtl ? -1 : 1) * this.viewport.clientWidth * .8, behavior: this.behavior });
    }
    update() {
      const max = this.viewport.scrollWidth - this.viewport.clientWidth;
      const position = Math.abs(this.viewport.scrollLeft);
      this.querySelector('[data-option-controls]').hidden = max <= 2;
      this.previous.disabled = position <= 2;
      this.next.disabled = position >= max - 2;
    }
  }
  customElements.define('rdb-option-carousel', RdbOptionCarousel);
})();
